<template>
<div>

	<div class="d-flex justify-content-between align-items-center mb-2">

		<info-box v-if="component.bid_component_id > 0" label="Direct Value" :value="componentDirectValue.toCurrency()"></info-box>

		<div>
			<button v-if="!isFinalized" type="button" v-on:click="deleteComponent" class="btn btn-danger btn-sm mr-1" :disabled="childComponents && childComponents.length > 0" :title="childComponents && childComponents.length > 0 ? 'You need to delete all child components before deleting the parent' : 'Delete the component'">
				<i class="fa fa-trash"></i>
				Delete Component
			</button>

			<button v-if="!isFinalized && component.bid_component_id > 0 && hasDerivativeComponents" type="button" v-on:click="syncDerivativeComponents" class="btn btn-primary btn-sm">
				<i class="fa fa-sync"></i>
				Sync Derivative Components
			</button>
		</div>

	</div>

	<accordion :heading="component.bid_component_id > 0 ? 'Component Details' : 'Category Details'">
		<p v-if="'JobBidComponentCopiedFrom' in component && component.JobBidComponentCopiedFrom" class="text-muted">
			Copied from {{ component.JobBidComponentCopiedFrom.item_number }}: {{ component.JobBidComponentCopiedFrom.name}}
		</p>
		<div v-else style="margin-bottom: 10px"></div>

		<field label="Name"
			col-class="col-sm-5 col-md-4"
			:align-label-right="true"
			v-model="componentFields.name"
			:readonly="isFinalized"
			:params="{
				type: 'text',
				maxlength: 80
			}"
			v-on:change="saveFields">
		</field>

		<template v-if="component.bid_component_id > 0">
			<component-detail-fields :values="componentFields"
				col-class="col-sm-5 col-md-4"
				:is-finalized="isFinalized"
				v-on:change="detailsFieldsChange">
			</component-detail-fields>
			
		</template>

	</accordion>
	<template v-if="component.bid_component_id > 0">

		<accordion :heading="'Material (' + materials.length + ')'">

			<material-section
				:materials="materials"
				:component="component"
				billing-document-object-resource="JobBidComponentObject"
				:billing-document-assemblies="assemblies"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">	
			</material-section>

		</accordion>

		<accordion :heading="'Labour (' + labour.length + ')'">

			<labour-section
				:labour="labour"
				:component="component"
				:hours-cap="componentFields.hours_cap"
				:total-material-labour-hours="totalMaterialLabourHours"
				:total-assembly-labour-task-hours="totalAssemblyLabourTaskHours"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels"
				v-on:hoursCapChanged="hoursCapChanged">
			</labour-section>
			
		</accordion>

		<accordion :heading="'Equipment (' + equipment.length + ')'">

			<equipment-section
				:equipment="equipment"
				:component="component"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">
			</equipment-section>
			
		</accordion>

		<accordion :heading="'Supplier Quotes (' + supplierQuotes.length + ')'">

			<supplier-quotes-section
				:supplier-quotes="supplierQuotes"
				:component="component"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">
			</supplier-quotes-section>
			
		</accordion>

		<accordion :heading="'Additional Factors & Items (' + additionalFactors.length + ')'">

			<additional-factors-section 
				:additional-factors="additionalFactors"
				:component="component"
				:is-finalized="isFinalized"
				v-on:modelChanged="updateObjectModels">		
			</additional-factors-section>
			
		</accordion>
	</template>

	<accordion :heading="'Child Components (' + childComponents.length + ')'" :stay-open="component.bid_component_id == 0">

		<div class="d-flex justify-content-between align-items-center  mt-2">

			<div>
				<p v-if="childComponents.length == 0" class="mb-0">No child components</p>
			</div>
			<div>
				<button v-if="!isFinalized && childComponents.length > 0"
					type="button"
					class="btn btn-sm mr-2"
					:class="sortChildComponentsEnabled ? 'btn-primary' : 'btn-outline-secondary'"
					v-on:click="sortChildComponentsEnabled = !sortChildComponentsEnabled">
					<i class="fa fa-sort mr-1"></i>
					{{ sortChildComponentsEnabled ? 'Done' : 'Reorder' }}
				</button>

				<button v-if="!isFinalized" class="btn btn-success" v-on:click="addComponent">
					<i class="fa fa-plus"></i>
					Add Component
				</button>
			</div>
		</div>
		
		<draggable v-if="childComponents.length > 0"
			v-model="childComponents" 
			class="list-group mt-2"
			:disabled="!sortChildComponentsEnabled || isFinalized"
			:options="{handle:'.handle'}"
			v-on:end="updateOrder">

			<li v-for="comp in childComponents"
				style="padding-bottom: 5px;padding-top: 5px;"
				class="list-group-item">

				<div class="d-flex justify-content-between align-items-center">
					<div v-if="sortChildComponentsEnabled && !isFinalized" class="handle mr-2" title="Drag to reorder">
						<i class="fa fa-sort"></i>
					</div>

					<div style="flex-grow: 1; max-width: calc(100% - 250px);" class="clickable-item" v-on:click="openComponent(comp)">
						<i class="fa fa-link mr-2"></i>
						<span v-if="comp.item_number">{{ comp.item_number }}. </span>{{ comp.name }}
					</div>
					<div>
						<form-group label="Qty"
							col-class="col-auto"
							style="margin-bottom: 0;">

							<div class="d-flex align-items-center">

								<field style="width: 100px"
									:params="{type:'number'}"
									:readonly="isFinalized"
									v-model="comp.qty"
									v-on:change="qtyChange($event,comp.id)">
								</field>

								<div class="ml-2" style="width: 40px;">{{ comp.qty_unit }}</div>
							</div>

						</form-group>
					</div>
				</div>
			</li>
		</draggable>

		
	</accordion>

	<modal-dialog :modal-visible="confirmDeleteComponentModalVisible"
		title="Delete Component?"
		confirm-button-text="Yes, Delete"
		button-class="btn-danger"
		v-on:closeModal="confirmDeleteComponentModalVisible = false"
		v-on:confirm="confirmDeleteComponent">

		Are you sure you want to delete this component?

	</modal-dialog>

	<modal-dialog :modal-visible="confirmSyncDerivativeComponentsModal.visible"
		title="Sync Derivative Components?"
		:submit-visible="confirmSyncDerivativeComponentsModal.derivativeComponents.length > 0"
		confirm-button-text="Yes, Sync"
		:working="confirmSyncDerivativeComponentsModal.working"
		v-on:closeModal="confirmSyncDerivativeComponentsModal.visible = false"
		v-on:confirm="confirmSyncDerivativeComponents">

		<template v-if="confirmSyncDerivativeComponentsModal.derivativeComponents.length > 0">
			<p>Are you sure you want to sync all derivative components? All components copied from this component will be re-copied to match the elements added to this. Child components will be left untouched. 
			</p>

			<h4>Copied Components:</h4>

			<ul>
				<li v-for="comp in confirmSyncDerivativeComponentsModal.derivativeComponents">
					<template v-if="'ParentJobBidComponent' in comp && comp.ParentJobBidComponent">{{ comp.ParentJobBidComponent.item_number }}: {{ comp.ParentJobBidComponent.name }} -> </template>
					{{ comp.item_number }}: {{ comp.name }}
				</li>
			</ul>
		</template>
		<p v-else>There are no components derived from this component</p>

	</modal-dialog>
</div>
</template>
<script type="text/javascript">

import Accordion from "@/components/Accordion"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"
import InfoBox from "@/components/InfoBox"

import ComponentDetailFields from "@/pages/project/project_tabs/bidding/components/ComponentDetailFields"

import draggable from 'vuedraggable'

import AdditionalFactorsSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/AdditionalFactorsSection"
import MaterialSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/MaterialSection"
import SupplierQuotesSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/SupplierQuotesSection"
import EquipmentSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/EquipmentSection"
import LabourSection from "@/pages/project/project_tabs/bidding/components/bid_component_sections/LabourSection"

import ProjectBidMixins from "@/mixins/ProjectBidMixins.js"
import InvoiceMixins from "@/mixins/InvoiceMixins.js"

export default {
	name: "ProjectBidComponent",
	components: {
		Accordion,
		TableList,
		ModalDialog,
		Field,
		InfoBox,
		AdditionalFactorsSection,
		MaterialSection,
		SupplierQuotesSection,
		EquipmentSection,
		LabourSection,
		FormGroup,
		draggable,
		ComponentDetailFields
	},
	mixins: [ProjectBidMixins,InvoiceMixins],
	props: ["component","isFinalized"],
	data() {
		return {
			componentFields: {
				name: "",
				description: "",
				item_number: null,
				spec_number: null,
				hours_cap: null,
				qty: 1,
				qty_unit: 'each',
				is_reverse_calculated: 0
			},
			componentQty: 1,
			materials: [],
			assemblies: [],
			equipment: [],
			labour: [],
			additionalFactors: [],
			supplierQuotes: [],
			childComponents: [],
			sortChildComponentsEnabled: false,
			hasDerivativeComponents: false,
			confirmDeleteComponentModalVisible: false,
			confirmSyncDerivativeComponentsModal: {
				visible: false,
				derivativeComponents: [],
				working: false
			}
		}
	},
	watch: {
		component() {
			this.initComponent()
			this.initObjects()
			this.initSubComponents()
			this.sortChildComponentsEnabled = false
		}	
	},
	computed: {
		totalMaterialLabourHours() {
			return this.materials.reduce((acc,item) => acc + (+item.labour_hrs * +item.qty),0)
		},
		totalAssemblyLabourTaskHours() {
			return this.assemblies.reduce((acc, assembly) => {
				let unit_type = ""
				if ("unit_type" in assembly)
					unit_type = assembly.unit_type

				let labourHrsPerAssembly = 0
				if (unit_type != 'Unit') {
					labourHrsPerAssembly = +assembly.labour_hrs_per_assembly
						+ (+assembly.labour_hrs_per_unit * +assembly.linear_qty)
						+ (+assembly.labour_hrs_per_segment * +assembly.num_segments)
				}
				else
					labourHrsPerAssembly = +assembly.labour_hrs_per_assembly

				return acc + labourHrsPerAssembly * +assembly.qty
			}, 0)
		},
		componentDirectValue() {
			let total = 0, vm = this
			
			total += this.materials
				.map(mat => {
					if (!("rate" in mat) || +mat.rate == 0) {
						if ("material_price" in mat)
							mat.rate = +mat.material_price / +mat.per
						else if ("price" in mat)
							mat.rate = +mat.price / +mat.per

					} 

					return mat
				})
				.reduce((acc,item) => {

					let totalRounded = +item.rate * +item.qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			total += this.equipment.reduce((acc,item) => {

					let totalRounded = +item.rate * +item.qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			total += this.labour.reduce((acc,item) => {

					let qty = +item.hrs
					if ("hrs_per_person" in item && vm.componentFields.hours_cap == 0)
						qty = +item.hrs_per_person * +item.crew

					let totalRounded = +item.rate * qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			total += this.supplierQuotes.reduce((acc,item) => {

					let totalRounded = +item.rate * +item.qty * +item.markup * +item.price_adjustment
					totalRounded = totalRounded.round(2)

					return acc + totalRounded

				},0)

			//total += this.additionalFactorsTotal

			let mutatedAdditionalFactors = this.additionalFactors.map(factor => {
				return vm.mutateAdditionalFactor(factor,vm.component.id)
			})

			let AFtotal = mutatedAdditionalFactors.reduce((acc,item) => acc + item.totalRaw.round(2),0)

			total += AFtotal

			return total
		}
	},
	methods: {
		openComponent(comp) {
			this.$emit("toComponent",comp)
		},
		initComponent() {
			let vm = this

			Object.keys(this.componentFields).forEach(key => {
				vm.componentFields[key] = vm.component[key]
			})
		},
		initSubComponents() {
			if (this.component.JobBidComponent)
				this.childComponents = this.component.JobBidComponent
			else
				this.childComponents = []
			this.checkHasDerivativeComponents()
		},
		checkHasDerivativeComponents() {
			if (!this.component.id || this.component.bid_component_id <= 0) {
				this.hasDerivativeComponents = false
				return
			}
			let vm = this
			this.getJSON("JobBidComponent/get", {
				filters: [["copied_from_bid_component_id", this.component.id]],
				limit: 1
			}).then(response => {
				vm.hasDerivativeComponents = response.success && response.data && response.data.length > 0
			})
		},
		initObjects(objects,type) {
			let vm = this

			let objMap = {
				Material: "materials",
				Assembly: "assemblies",
				EmployeeClassification: "labour",
				AdditionalFactor: "additionalFactors",
				Equipment: "equipment",
				SupplierQuote: 'supplierQuotes'
			}

			if (!type) {
				//reset all
				this.materials = []
				this.labour = []
				this.additionalFactors = []
				this.equipment = []
				this.supplierQuotes = []
				this.assemblies = []
			}
			else //only reset the defined type
				this[objMap[type]] = []

			let sourceObjects
			if (objects) 
				sourceObjects = objects
			else if ("JobBidComponentObject" in this.component && this.component.JobBidComponentObject) 
				sourceObjects = this.component.JobBidComponentObject

			if (sourceObjects) {

				sourceObjects.forEach(obj => {

					let objToAdd = {}

					try {
						objToAdd = JSON.parse(obj.field_values_json)
					}
					catch (e) {}

					objToAdd.qty = obj.qty
					objToAdd.rate = obj.rate
					objToAdd.price_adjustment = obj.price_adjustment
					objToAdd.markup = obj.markup
					objToAdd.objectRecordId = obj.id
					objToAdd.object_id = obj.object_id
					objToAdd.itemType = "itemType" in obj ? obj.itemType : null

					if (obj.type && obj.type in objMap)
						vm[objMap[obj.type]].push(objToAdd)
				})
			}
		},
		updateOrder() {
			let payload = {
				records: JSON.stringify(this.childComponents.map((comp,index) => {
					return {
						order_index: index,
						id: comp.id
					}
				}))
			}

			this.postData("JobBidComponent/save-batch",payload)
		},
		hoursCapChanged(value) {
			this.componentFields.hours_cap = value
			this.saveFields()
		},
		detailsFieldsChange(values) {
			this.componentFields = values

			this.saveFields()
		},
		saveFields() {
			let vm = this

			this.$nextTick(() => {

				let payload = vm.componentFields

				payload.id = vm.component.id

				vm.postData("JobBidComponent/save",payload,true).then(response => {
					if (response.success)
						vm.alertSaved("Changes saved")
				})

			})
		},
		addComponent() {
			this.$bus.$emit("addComponent",this.component.id)
		},
		deleteComponent() {
			this.confirmDeleteComponentModalVisible = true
		},
		confirmDeleteComponent() {
			let vm = this

			this.postData("JobBidComponent/delete",{id: this.component.id},true).then(response => {
				if (response.success) {
					vm.confirmDeleteComponentModalVisible = false
					vm.$bus.$emit("componentDeleted",vm.component)
				}
			})
		},
		syncDerivativeComponents() {
			this.confirmSyncDerivativeComponentsModal.visible = true

			let vm = this

			let params = {
				filters: [
					["copied_from_bid_component_id", this.component.id]
				],
				include: ["JobBidComponent"]
			}

			this.getJSON("JobBidComponent/get",params).then(response => {
				if (response.success)
					vm.confirmSyncDerivativeComponentsModal.derivativeComponents = response.data
			})
		},
		confirmSyncDerivativeComponents() {
			let vm = this

			let promises = []

			this.confirmSyncDerivativeComponentsModal.working = true

			let derivativeComponents = this.confirmSyncDerivativeComponentsModal.derivativeComponents

			//delete the old components
			derivativeComponents.forEach(comp => {
				promises.push(vm.postData("JobBidComponent/delete",{id: comp.id}))
			})

			Promise.all(promises).then(responses => {
				promises = []

				//re-clone the components
				derivativeComponents.forEach(comp => {

					let payload = {
						parentComponentId: comp.bid_component_id,
						componentId: vm.component.id
					}

					promises.push(vm.postData("JobBidComponent/clone-component",payload))
				})

				Promise.all(promises).then(responses => {
					promises = []

					let fields = ["name","description","item_number","spec_number","order_index","can_have_time_log","hours_cap","qty","qty_unit","is_reverse_calculated"]

					//reset all the detail fields
					responses.forEach((response,index) => {
						let payload = {
							id: response.data.record_id
						}

						fields.forEach(field => {
							payload[field] = derivativeComponents[index][field]
						})

						promises.push(vm.postData("JobBidComponent/save",payload))
					})

					Promise.all(promises).then(response => {
						vm.confirmSyncDerivativeComponentsModal.working = false
						vm.confirmSyncDerivativeComponentsModal.visible = false
						vm.alertSuccess(derivativeComponents.length + " derivative components synced!")

					})
				})
			})
		},
		updateObjectModels() {
			let vm = this

			this.fetchBidObjects(this.component.bid_id).then(responseObjects => {
				//filter to just this component
				let compObjects = responseObjects.filter(obj => obj.bid_component_id == vm.component.id)

				vm.initObjects(compObjects)
			})
		},
		fetchComponentObjects(typeFilter) {
			let vm = this

			let params = {
				filters: [
					["bid_component_id",vm.component.id]
				]
			}

			if (typeFilter)
				params.filters.push(["type",typeFilter])

			return new Promise((resolve,reject) => {

				vm.getJSON("JobBidComponentObject/get",params).then(response => {

					vm.initObjects(response.data,typeFilter)

					resolve(response)
				})

			})
		}
	},
	created() {
		let vm = this
		this.initComponent()
		this.initObjects()
		this.initSubComponents()

		if (this.$store.unitMeasurementOptions.length == 0) 
			this.fetchMeasurementUnits()
	}
}
</script>

<style scoped>
.handle {
	cursor: grab;
	touch-action: none;
}

.clickable-item {
	cursor: pointer;
}

.clickable-item:hover {
	opacity: 0.7
}
</style>